<script setup lang="ts">
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { PriceCalendar } from '@apps/main/new-desktop/modules/priceCalendar';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import {
  AppStandardModal,
  AppMessageBox,
  AppSvgIcon,
  AppFlicker,
} from '@libs/ui';

import { usePeekPriceStore } from '../stores/peekPriceStore';

interface PeekPriceModalEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
}

const emit = defineEmits<PeekPriceModalEmits>();

const peekPriceStore = usePeekPriceStore();
const statsigEventsStore = useStatsigEventsStore();

function onSelectDate(date: Date): void {
  statsigEventsStore.logHotelSelectionPeekPriceModalDateSelected();
  statsigEventsStore.logCoreDateChanged({
    source: CoreEventSource.HotelSelection,
  });
  peekPriceStore.preselectDate(date);
}

function onSelectMonth(date: Date): void {
  statsigEventsStore.logHotelSelectionPeekPriceModalMonthSelected();
  peekPriceStore.selectMonth(date);
}
</script>

<template>
  <AppStandardModal @close="emit('cancel')" @confirm="emit('confirm')">
    <template #title>
      {{ $t('planner.more_dates_and_prices_for_trip') }}
    </template>
    <template #body>
      <div class="peek-price-modal">
        <div class="prices-for-hotel-text">
          {{ $t('planner.prices_in_calendar') }}
          <span class="hotel-name-text">{{ peekPriceStore.hotelName }}.</span>
        </div>

        <AppMessageBox
          :header-text="$t('planner.calendar_shows_date_price_header')"
          :text="$t('planner.calendar_shows_date_price')"
          message-type="brand-lagoon"
        >
          <template #left-icon>
            <AppSvgIcon name="lightbulb" />
          </template>
        </AppMessageBox>

        <PriceCalendar
          v-if="peekPriceStore.priceCalendarState.anyResultsLoaded()"
          :selected-month="peekPriceStore.selectedMonth"
          :months="peekPriceStore.months"
          :price-calendar-state="peekPriceStore.priceCalendarState"
          :show-prices="true"
          :allow-hover-states="true"
          @select-month="onSelectMonth"
          @select-date="onSelectDate"
        />
        <AppFlicker
          v-else
          height="spacing-32"
          width="spacing-80"
          border-radius="border-radius-12"
          class="flicker"
        />
      </div>
    </template>

    <template #button>
      {{ $t('planner.done') }}
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.peek-price-modal {
  width: 100%;
  height: 100%;

  .prices-for-hotel-text {
    margin-bottom: var(--spacing-24);

    @include font-label-m-light;
    color: var(--color-neutral-70);

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
    }
  }

  .flicker {
    margin-top: var(--spacing-24);
  }
}
</style>
